/**
 * View-model helpers for the watchlist Changes tab: what the user can sort, group and filter
 * by, and how a loaded list of changes becomes the flat rows the virtualized list renders.
 * Pure functions, no i18n or DOM: callers pass in the label functions.
 */

export const CHANGE_SORTS = ['unread_first', 'newest', 'oldest', 'entity', 'entity_type', 'reason']
export const CHANGE_GROUPINGS = ['none', 'day', 'entity_type', 'reason']
export const CHANGE_ENTITY_TYPES = ['brand', 'presentation', 'price', 'spc']
export const CHANGE_REASONS = ['pinned_brand', 'pinned_presentation', 'fav_group', 'filter', 'filter_exit', 'price', 'spc']

// Local calendar day (not UTC), so "Today" and "Yesterday" mean the user's today and yesterday.
export const dayKey = timestamp => {
  const day = new Date(timestamp)
  return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`
}

const changeRow = change => ({ type: 'change', key: `change-${change.id}`, change })
const headerRow = (key, label, count) => ({ type: 'header', key: `header-${key}`, label, count })
const dividerRow = version => ({ type: 'divider', key: `version-${version.number}`, version })

const groupKeyOf = (change, groupBy) => {
  switch (groupBy) {
    case 'day': return dayKey(change.createdAt)
    case 'entity_type': return change.entity?.type ?? 'unknown'
    case 'reason': return change.matchReason ?? 'unknown'
    default: return 'all'
  }
}

// Time-ordered lists get a divider wherever the watchlist's criteria changed, so a reader can
// tell "this was caught under the old filters" from "under the new ones". A boundary is only
// shown when it can't be hiding unloaded changes (older ones may still load on demand).
const withVersionDividers = ({ changes, sort, versions, hasOlder }) => {
  const isNewestFirst = sort === 'newest'
  const oldestLoaded = Math.min(...changes.map(change => new Date(change.createdAt).getTime()))

  const boundaries = versions
    .filter(version => version.number > 1)
    .map(version => ({ version, at: new Date(version.effectiveFrom).getTime() }))
    .filter(boundary => !hasOlder || boundary.at > oldestLoaded)
    .sort((a, b) => isNewestFirst ? b.at - a.at : a.at - b.at)

  const rows = []
  let next = 0

  for (const change of changes) {
    const moment = new Date(change.createdAt).getTime()

    while (next < boundaries.length && (isNewestFirst ? moment < boundaries[next].at : moment >= boundaries[next].at)) {
      rows.push(dividerRow(boundaries[next].version))
      next++
    }
    rows.push(changeRow(change))
  }

  for (; next < boundaries.length; next++) rows.push(dividerRow(boundaries[next].version))

  return rows
}

/**
 * @param {object} options
 * @param {Array} options.changes changes in the order the server returned them
 * @param {string} options.sort one of CHANGE_SORTS
 * @param {string} options.groupBy one of CHANGE_GROUPINGS
 * @param {Array} [options.versions] criteria versions (any order), for dividers
 * @param {boolean} [options.hasOlder] whether older changes can still be loaded
 * @param {{ unread: (count: number) => string, earlier: string, group: (groupBy: string, key: string) => string }} options.labels
 * @returns {Array<{ type: 'header' | 'change' | 'divider', key: string }>}
 */
export const buildChangeRows = ({ changes, sort = 'unread_first', groupBy = 'none', versions = [], hasOlder = false, labels }) => {
  if (!changes.length) return []

  if (groupBy !== 'none') {
    const groups = new Map()
    for (const change of changes) {
      const key = groupKeyOf(change, groupBy)
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(change)
    }
    return [...groups].flatMap(([key, items]) => [
      headerRow(`${groupBy}-${key}`, labels.group(groupBy, key), items.length),
      ...items.map(changeRow)
    ])
  }

  if (sort === 'unread_first') {
    const unread = changes.filter(change => !change.seenAt)
    const read = changes.filter(change => change.seenAt)
    return [
      ...(unread.length ? [headerRow('unread', labels.unread(unread.length), unread.length), ...unread.map(changeRow)] : []),
      ...(read.length ? [...(unread.length ? [headerRow('earlier', labels.earlier, read.length)] : []), ...read.map(changeRow)] : [])
    ]
  }

  if (sort === 'newest' || sort === 'oldest') {
    return withVersionDividers({ changes, sort, versions, hasOlder })
  }

  return changes.map(changeRow)
}
